"use client";

import { useId } from "react";
import { cn } from "@/lib/utils/cn";
import type { WatchlistTier } from "@prisma/client";

const TIER_OPTIONS: Array<{ value: WatchlistTier; label: string }> = [
  { value: "active", label: "Active" },
  { value: "watching", label: "Watching" },
  { value: "monitor", label: "Monitor" },
];

interface TierSelectProps {
  value: WatchlistTier;
  onChange: (tier: WatchlistTier) => void;
  disabled?: boolean;
  className?: string;
  "aria-label"?: string;
  /** Hide the (?) explainer popover. Default: shown. */
  hideExplainer?: boolean;
}

export function TierSelect({
  value,
  onChange,
  disabled,
  className,
  "aria-label": ariaLabel,
  hideExplainer,
}: TierSelectProps) {
  const explainerId = useId();
  return (
    <div className="flex items-center gap-1.5">
      <select
        value={value}
        onChange={(e) => onChange(e.target.value as WatchlistTier)}
        disabled={disabled}
        aria-label={ariaLabel ?? "Tier"}
        aria-describedby={hideExplainer ? undefined : explainerId}
        className={cn(
          "h-9 rounded-md border border-input bg-transparent px-2 text-sm shadow-sm",
          "transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
          "disabled:cursor-not-allowed disabled:opacity-50",
          className,
        )}
      >
        {TIER_OPTIONS.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
      {!hideExplainer ? <TierExplainer id={explainerId} /> : null}
    </div>
  );
}

/**
 * Tier explainer popover — uses native <details> to stay HTML-only / keyboard
 * accessible / no client state. Click or Enter on the (?) icon toggles the
 * three-card breakdown of cost expectations per tier.
 */
function TierExplainer({ id }: { id: string }) {
  return (
    <details className="relative inline-block">
      <summary
        aria-label="What does each tier mean?"
        className={cn(
          "list-none cursor-pointer select-none flex items-center justify-center",
          "w-5 h-5 rounded-full border border-surface-light/40 bg-transparent",
          "text-[11px] font-bold text-muted-foreground hover:text-foreground hover:border-blue/40",
          "transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
          // Hide the disclosure marker in webkit
          "[&::-webkit-details-marker]:hidden",
        )}
      >
        ?
      </summary>
      <div
        id={id}
        role="region"
        aria-label="Tier explainer"
        className={cn(
          "absolute right-0 z-50 mt-2 w-[300px] max-w-[80vw]",
          "rounded-xl border border-surface-light/30 bg-surface shadow-2xl p-3 space-y-2",
        )}
      >
        <TierCard
          name="Monitor"
          tagline="Free"
          description="We track price + earnings dates only. No analysis. Switch to Watching when you're ready to start."
          accent="text-muted-foreground"
        />
        <TierCard
          name="Watching"
          tagline="Light analysis weekly"
          description="Weekly Light analysis only: fundamentals + valuation deltas vs the last deep run, plus the 5 cross-cutting analyzers. No fresh data refresh; uses last week's data."
          accent="text-blue"
        />
        <TierCard
          name="Active"
          tagline="Full Deep + Debate + Synthesis weekly"
          description="Full weekly Deep analysis on Fundamental + Valuation, plus all 7 analyzer Lights, plus Compass Debate (5 personas × 10 rounds), plus the Monday Synthesis brief. Default for stocks you're researching to act on."
          accent="text-gold"
        />
      </div>
    </details>
  );
}

function TierCard({
  name,
  tagline,
  description,
  accent,
}: {
  name: string;
  tagline: string;
  description: string;
  accent: string;
}) {
  return (
    <div className="rounded-md border border-surface-light/20 bg-navy-mid/40 p-2.5">
      <div className="flex items-baseline justify-between gap-2 mb-1">
        <span className={cn("text-xs font-semibold", accent)}>{name}</span>
        <span className="text-[10px] text-muted-foreground">{tagline}</span>
      </div>
      <p className="text-[11px] leading-relaxed text-foreground/80">
        {description}
      </p>
    </div>
  );
}
